import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, ExternalLink, Loader2 } from "lucide-react";

const KIND_LABELS = {
  leads: "Leads",
  estimates: "Estimates",
  jobs: "Jobs",
  invoices: "Invoices",
  customers: "Customers",
  stale_estimates: "Stale estimates",
  stale_leads: "Stale leads",
  overdue_invoices: "Overdue invoices",
  upcoming_jobs: "Upcoming jobs (7d)",
  historical_invoices: "Historical imports",
};

const ROUTE_MAP = {
  lead: (id) => `/app/leads/${id}`,
  estimate: (id) => `/app/estimates/${id}`,
  customer: (id) => `/app/customers/${id}`,
  invoice: (id) => `/app/invoices/${id}`,
  job: (id) => `/app/jobs/${id}`,
};

function fmt(v) {
  if (v == null) return "—";
  if (typeof v === "number") {
    if (v > 100) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    return v.toString();
  }
  if (typeof v === "string" && v.length > 30) return v.slice(0, 30) + "…";
  return String(v);
}

export default function CoachSourcesDrawer({ kind, period, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    api.get(`/business-coach/sources/${kind}?period=${period}`)
      .then(({ data }) => { if (!stop) setData(data); })
      .catch((e) => { if (!stop) setErr(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, [kind, period]);

  const label = KIND_LABELS[kind] || kind;
  const records = data?.records || [];
  const entityType = data?.entity_type;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="coach-sources-drawer" onClick={onClose}>
      <div
        className="w-full sm:w-[560px] bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00]">Coach Sources</div>
            <div className="font-heading text-xl font-bold">{label}</div>
            {data && (
              <div className="text-xs text-zinc-500 mt-0.5">
                {data.count} record{data.count === 1 ? "" : "s"} · {data.period}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="coach-sources-close"
                  className="p-2 rounded-md hover:bg-zinc-100 text-zinc-600">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          )}
          {err && <div className="text-sm text-rose-600" data-testid="coach-sources-error">{err}</div>}
          {!loading && !err && records.length === 0 && (
            <div className="text-sm text-zinc-500" data-testid="coach-sources-empty">
              No records match this filter.
            </div>
          )}
          {!loading && records.length > 0 && (
            <ul className="space-y-2" data-testid="coach-sources-list">
              {records.map((r, i) => (
                <SourceRow key={r.id || i} row={r} entityType={entityType} index={i}/>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceRow({ row, entityType, index }) {
  const link = ROUTE_MAP[entityType]?.(row.id);
  const title =
    row.name || row.customer_name ||
    (row.invoice_number ? `Invoice #${row.invoice_number}` : "") ||
    (row.estimate_number ? `Estimate #${row.estimate_number}` : "") ||
    "Record";
  const meta = [];
  if (row.status) meta.push(row.status);
  if (row.source) meta.push(row.source);
  if (row.service_name) meta.push(row.service_name);
  if (row.days_open) meta.push(`${row.days_open}d open`);
  if (row.days_old) meta.push(`${row.days_old}d old`);
  return (
    <li className="border border-zinc-200 rounded-lg p-3 hover:border-[#FF5A00] hover:bg-orange-50/40 transition"
        data-testid={`coach-sources-row-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{title}</div>
          {meta.length > 0 && <div className="text-xs text-zinc-500 mt-0.5">{meta.join(" · ")}</div>}
          <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-zinc-600">
            {row.total != null && <span>Total {fmt(row.total)}</span>}
            {row.balance != null && <span>Balance {fmt(row.balance)}</span>}
            {row.estimated_value != null && <span>Value {fmt(row.estimated_value)}</span>}
            {row.due_date && <span>Due {row.due_date.slice(0, 10)}</span>}
            {row.invoice_date && <span>Dated {row.invoice_date.slice(0, 10)}</span>}
            {row.scheduled_at && <span>Scheduled {row.scheduled_at.slice(0, 10)}</span>}
            {row.completed_at && <span>Completed {row.completed_at.slice(0, 10)}</span>}
          </div>
        </div>
        {link && (
          <a href={link} target="_blank" rel="noreferrer"
             data-testid={`coach-sources-open-${index}`}
             className="text-[#FF5A00] hover:underline shrink-0 text-xs flex items-center gap-1">
            Open <ExternalLink className="w-3 h-3"/>
          </a>
        )}
      </div>
    </li>
  );
}
